/**
 * Le passage d'un exercice par l'élève : énoncé, brouillon, envoi, résultat.
 *
 * Ce composant ne sait **rien** des bonnes réponses, et pas parce qu'il évite de
 * les regarder : le type que le serveur lui envoie n'a aucun champ où en loger
 * une. La garantie court sans interruption depuis `catalog.public` — chaque
 * maillon est un type sans champ plutôt qu'un filtre à appliquer.
 *
 * Il ne décide pas non plus de la note. `passed`, `score_pct` et `attempts_left`
 * viennent du serveur : recalculer un pourcentage ici donnerait un second
 * barème, et c'est celui de l'écran que l'élève croirait.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useSaveAnswers,
  useStartAttempt,
  useSubmitAttempt,
  type Attempt,
  type AttemptResult,
  type DraftAnswers,
  type ExerciseState,
} from "@/features/learning/api/useLearning";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { asDocument } from "@/shared/richtext/document";
import { RichContent } from "@/shared/richtext/RichContent";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";

interface AttemptRunnerProps {
  exercise: ExerciseState;
}

/**
 * Les identifiants de propositions cochées, dans la forme que le serveur attend.
 *
 * `{"choice_ids": [...]}` est le contrat de docs/05 §6, et il cite les
 * propositions **par identifiant** : c'est ce qui permet à l'enseignant de
 * corriger une faute de frappe sans invalider les copies déjà rendues. Envoyer
 * le libellé ferait de chaque correction une amnésie.
 */
function toAnswer(choiceIds: string[]): Record<string, unknown> {
  return { choice_ids: choiceIds };
}

export function AttemptRunner({ exercise }: AttemptRunnerProps) {
  const { t } = useTranslation("learning");
  const [attempt, setAttempt] = useState<Attempt | undefined>(undefined);
  const [result, setResult] = useState<AttemptResult | undefined>(undefined);
  const [choices, setChoices] = useState<Record<string, string[]>>({});

  const start = useStartAttempt();
  const save = useSaveAnswers();
  const submit = useSubmitAttempt();

  const beginAttempt = () => {
    start.mutate(exercise.exercise_id, {
      onSuccess: (started) => {
        setAttempt(started);
        setResult(undefined);
        // Le brouillon déjà enregistré est rechargé : un élève qui rafraîchit
        // sa page retrouve ses réponses, et le serveur lui rend la tentative
        // ouverte plutôt que d'en consommer une seconde.
        setChoices(
          Object.fromEntries(
            started.saved_answers.map((answer) => [
              answer.question_id,
              readChoiceIds(answer.value),
            ]),
          ),
        );
      },
    });
  };

  const toggle = (questionId: string, choiceId: string, multiple: boolean) => {
    setChoices((current) => {
      const selected = current[questionId] ?? [];
      if (!multiple) {
        return { ...current, [questionId]: [choiceId] };
      }
      return {
        ...current,
        [questionId]: selected.includes(choiceId)
          ? selected.filter((item) => item !== choiceId)
          : [...selected, choiceId],
      };
    });
  };

  const send = () => {
    if (attempt === undefined) {
      return;
    }
    const answers: DraftAnswers = Object.fromEntries(
      Object.entries(choices).map(([questionId, selected]) => [questionId, toAnswer(selected)]),
    );
    save.mutate(
      { attemptId: attempt.id, answers },
      {
        onSuccess: () => {
          submit.mutate(attempt.id, {
            onSuccess: (submitted) => {
              setResult(submitted);
              setAttempt(undefined);
            },
          });
        },
      },
    );
  };

  if (result !== undefined) {
    return <AttemptOutcome result={result} onRetry={beginAttempt} canRetry={canRetry(result)} />;
  }

  if (attempt === undefined) {
    return (
      <Card title={exercise.title}>
        <div className="space-y-3">
          <ExerciseSummary exercise={exercise} />
          {start.isError && <ErrorState error={start.error} />}
          <Button onClick={beginAttempt} disabled={start.isPending || exercise.attempts_left === 0}>
            {t("attempt.start")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title={attempt.title}>
      <div className="space-y-4">
        {attempt.instructions !== null && (
          <div className="text-sm text-slate-600 dark:text-slate-400">
            <RichContent document={asDocument(attempt.instructions)} />
          </div>
        )}
        {attempt.questions.map((question) => {
          const multiple = question.type === "multiple_choice";
          return (
            // `aria-labelledby` plutôt qu'une `legend` : l'énoncé est un
            // document, et une `legend` ne peut pas porter des blocs. Un
            // `legend` masqué qui recopierait le texte marcherait — et ferait
            // exister l'énoncé **deux fois** dans la page, dont une copie que
            // personne ne relit quand l'autre change.
            <fieldset
              key={question.id}
              className="space-y-2"
              aria-labelledby={`prompt-${question.id}`}
            >
              <div id={`prompt-${question.id}`} className="text-sm font-medium">
                <RichContent document={asDocument(question.prompt)} />
              </div>
              {question.choices.map((choice) => (
                <label key={choice.id} className="flex items-center gap-2 text-sm">
                  <input
                    type={multiple ? "checkbox" : "radio"}
                    name={question.id}
                    value={choice.id}
                    checked={(choices[question.id] ?? []).includes(choice.id)}
                    onChange={() => {
                      toggle(question.id, choice.id, multiple);
                    }}
                  />
                  <span>{choice.label}</span>
                </label>
              ))}
            </fieldset>
          );
        })}
        {save.isError && <ErrorState error={save.error} />}
        {submit.isError && <ErrorState error={submit.error} />}
        <Button onClick={send} disabled={save.isPending || submit.isPending}>
          {t("attempt.submit")}
        </Button>
      </div>
    </Card>
  );
}

/**
 * `attempts_left === null` veut dire **illimité**, jamais « zéro ».
 *
 * Le champ est obligatoire dans le contrat pour que cette distinction soit
 * lisible : un `!attempts_left` écrit naturellement serait vrai dans les deux
 * cas, et fermerait un exercice que personne n'a limité (leçon de la phase 2).
 */
function canRetry(result: AttemptResult): boolean {
  return result.passed !== true && result.attempts_left !== 0;
}

function ExerciseSummary({ exercise }: { exercise: ExerciseState }) {
  const { t } = useTranslation("learning");
  return (
    // Une colonne sur un téléphone : à deux, les libellés « tentative » et
    // « essais restants » se coupent en trois lignes chacun sur 375 px, et le
    // bandeau qui devait rassurer devient le plus dense de l'écran.
    <dl className="grid grid-cols-1 gap-2 text-sm text-slate-600 sm:grid-cols-2 dark:text-slate-400">
      <dt>{t("attempt.passMark")}</dt>
      <dd>{t("attempt.percent", { value: exercise.passing_score })}</dd>
      <dt>{t("attempt.attemptsLeft")}</dt>
      <dd>
        {exercise.attempts_left === null ? t("attempt.unlimited") : String(exercise.attempts_left)}
      </dd>
      {exercise.best_score_pct !== null && (
        <>
          <dt>{t("attempt.bestScore")}</dt>
          <dd>{t("attempt.percent", { value: exercise.best_score_pct })}</dd>
        </>
      )}
    </dl>
  );
}

interface OutcomeProps {
  result: AttemptResult;
  onRetry: () => void;
  canRetry: boolean;
}

function AttemptOutcome({ result, onRetry, canRetry: retryable }: OutcomeProps) {
  const { t } = useTranslation("learning");

  // `awaiting_grading` est un troisième cas, et non « pas encore réussi » : la
  // copie attend un correcteur humain, et afficher un échec ferait lire comme
  // un verdict ce qui n'est qu'une attente.
  if (result.awaiting_grading) {
    return (
      <Card title={t("attempt.resultTitle")}>
        <Alert tone="info">{t("attempt.awaitingGrading")}</Alert>
      </Card>
    );
  }

  return (
    <Card title={t("attempt.resultTitle")}>
      <div className="space-y-3">
        <Alert tone={result.passed === true ? "success" : "warning"}>
          {result.passed === true ? t("attempt.passed") : t("attempt.failed")}
          {result.score_pct !== null && ` — ${t("attempt.percent", { value: result.score_pct })}`}
        </Alert>
        {result.feedback.length > 0 && (
          <ul className="space-y-1 text-sm">
            {result.feedback.map((item) => (
              <li key={item.question_id} className="flex gap-1 text-slate-600 dark:text-slate-400">
                <span>{item.is_correct === true ? "✓" : "✗"}</span>
                {item.explanation !== null && (
                  <RichContent document={asDocument(item.explanation)} />
                )}
              </li>
            ))}
          </ul>
        )}
        {retryable && <Button onClick={onRetry}>{t("attempt.retry")}</Button>}
      </div>
    </Card>
  );
}

/**
 * Relit un brouillon enregistré sans jamais faire confiance à sa forme.
 *
 * `value` est du JSON de forme libre : le serveur y range ce que le type de
 * question demande, et une valeur écrite par une version antérieure du client
 * peut ne pas ressembler à ce qu'on attend. Un `as string[]` planterait le
 * rendu ; ignorer ce qu'on ne reconnaît pas coûte au pire un brouillon vide.
 */
function readChoiceIds(value: Record<string, unknown>): string[] {
  const raw = value["choice_ids"];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((item): item is string => typeof item === "string");
}
